import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import SchedulingList from './pages/SchedulingList'
import SchedulingForm from './pages/SchedulingForm'
import PageDetail from './pages/PageDetail'
import PublicBooking from './pages/PublicBooking'
import BookingsDashboard from './pages/BookingsDashboard'
import TemplatesList from './pages/TemplatesList'
import TemplateForm from './pages/TemplateForm'
import CrmSettings from './pages/CrmSettings'
import CustomerPortalLogin from './pages/CustomerPortalLogin'
import CustomerPortalDashboard from './pages/CustomerPortalDashboard'
import RescheduleBooking from './pages/RescheduleBooking'
import Login from './pages/Login'
import './App.css'

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route 
            path="/scheduling" 
            element={
              <ProtectedRoute>
                <SchedulingList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/scheduling/new" 
            element={
              <ProtectedRoute>
                <SchedulingForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/scheduling/:id/edit" 
            element={
              <ProtectedRoute>
                <SchedulingForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/scheduling/:id" 
            element={
              <ProtectedRoute>
                <PageDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/scheduling/bookings" 
            element={
              <ProtectedRoute>
                <BookingsDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Meeting Templates */}
          <Route 
            path="/templates" 
            element={
              <ProtectedRoute>
                <TemplatesList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/templates/new" 
            element={
              <ProtectedRoute>
                <TemplateForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/templates/:id/edit" 
            element={
              <ProtectedRoute>
                <TemplateForm />
              </ProtectedRoute>
            } 
          />
          
          {/* CRM Settings */}
          <Route 
            path="/settings/crm" 
            element={
              <ProtectedRoute>
                <CrmSettings />
              </ProtectedRoute>
            } 
          />
          
          {/* Customer Portal */}
          <Route path="/portal" element={<CustomerPortalLogin />} />
          <Route path="/portal/dashboard" element={<CustomerPortalDashboard />} />
          
          {/* Public Routes */}
          <Route path="/schedule/:slug" element={<PublicBooking />} />
          <Route path="/schedule/:slug/reschedule" element={<RescheduleBooking />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}

function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
      {/* Navbar */}
      <nav className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xl font-bold text-white">Eureka</span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/login" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                Login
              </a>
              <a 
                href="/login" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-8">
            <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2"></span>
            Now with Eureka SSO Integration
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Professional Scheduling
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              Made Simple
            </span>
          </h1>
          
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            Create beautiful booking pages, manage your availability, and let others schedule time with you. 
            Seamlessly integrated with Eureka Labo.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="/login" 
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25"
            >
              Get Started
            </a>
            <a 
              href="/schedule/john-doe-30min" 
              className="w-full sm:w-auto px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-xl transition-all duration-200 border border-gray-700"
            >
              View Demo
            </a>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-20 text-left">
            <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-2xl p-6">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Smart Scheduling</h3>
              <p className="text-gray-400">Automatically check availability and prevent double bookings</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-2xl p-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Email Notifications</h3>
              <p className="text-gray-400">Automatic confirmations and reminders for all bookings</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-2xl p-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Eureka SSO</h3>
              <p className="text-gray-400">Seamless integration with your Eureka Labo account</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App
